import { _getLogger } from '@doxyme/analytics';
import ExtensionCapability from '../../capability';
import { Specs, attachHandler } from '@doxyme/extensions-common';
import Instance from '../../instance';

type Constraints = Specs.AppToken.Constraints;
type GetPayload = Specs.AppToken.GetPayload;

const { AppToken } = Specs;

const logger = _getLogger('appToken', {
  component: 'extensions',
  lib: 'bridge',
});

export type OnGetHandler = (manifestName: string) => Promise<string>;

/**
 * App token allows extensions to use the get and update the auth token within the host application.
 */
export default class AppTokenCapability extends ExtensionCapability<Constraints> {
  static readonly id = AppToken.id;

  readonly getHandlers: OnGetHandler[] = [];

  constructor() {
    super(AppToken);

    this.handlers.push([AppToken.Actions.Get, this.get.bind(this)]);
  }

  async get(
    payload: GetPayload | undefined,
    instance: Instance<Constraints>,
  ): Promise<void> {
    try {
      let token: string = '';
      for await (const handler of this.getHandlers) {
        token = await handler(instance.manifest.name);
      }

      // AppToken.Events.Update is @deprecated, it is here for backwards compatibility, will be removed
      instance.notify([AppToken.Events.Update, token]);
      instance.notify([
        AppToken.Events.GetStatus,
        { isSuccess: true, data: token },
      ]);
    } catch (error: unknown) {
      const errorMessage =
        (error as { message?: string } | undefined)?.message ?? `${error}`;

      logger.error(errorMessage, {
        manifest: { id: instance.manifest.id, name: instance.manifest.name },
        action: 'get',
        error,
      });

      instance.notify([
        AppToken.Events.GetStatus,
        { isSuccess: false, errorMessage },
      ]);
    }
  }

  onGet(handler: OnGetHandler): () => void {
    return attachHandler<OnGetHandler>(this.getHandlers, handler);
  }
}
